import type { AnySymbol as AnyViewSymbol } from '@core/symbol-legacy';
import type { Visibility } from '@core/visibility-types';

import type { AnyGroup } from './groups.types';
import type {
  ViewEmptyState,
  ViewFilterConditionId,
  ViewFilterDef,
  ViewFilterInputPayload,
  ViewFilterValue,
} from './view-types';

export type SortingDirection = 'asc' | 'desc';
export interface ViewsSorting {
  sortingId: string;
  direction: SortingDirection;
}

export enum FilterGroupOperator {
  And = 'and',
  Or = 'or',
}

export interface FilterPayload {
  filterId: string;
  condition: ViewFilterConditionId;
  inputs: ViewFilterInputPayload[];
}

export interface SwimlaneConfig {
  configId: string;
  hideEmpty: boolean;
}

export type ViewLayout =
  | { type: 'list'; fields: ViewField[] }
  | { type: 'table'; fields: ViewField[]; autoFit: boolean }
  | { type: 'board'; fields: ViewField[]; swimlane: SwimlaneConfig | null };

export interface GroupConfig {
  // Grouping id
  configId: string;
  hideEmpty: boolean;
  hidden: Array<AnyGroup['id']>;
}

export interface CreateViewPayload {
  name: string;
  layout: ViewLayout;
  symbol: AnyViewSymbol | null;
  group: GroupConfig | null;
  sorting: ViewsSorting | null;
  operator: `${FilterGroupOperator}`;
  filters: FilterPayload[];
  quickFiltersIds: string[] | null;
  visibility: Visibility;
  searchQuery: string | null;
}

export type GroupId = string | number | object;
export interface GroupParams {
  // Grouping id
  configId: string;
  // Group id
  value: GroupId;
}

export interface SwimlaneParams {
  type: 'board';
  swimlane: { configId: string; value: string | number | object; hideEmpty: boolean };
}

export interface SwimlaneRecordsParams {
  type: 'board';
  swimlane: { configId: string; value: string | number | object };
}

export type LayoutRecordsParams =
  | SwimlaneRecordsParams
  | { type: 'list' | 'table'; swimlane?: never };

export interface LayoutRecordsCountPayload {
  operator: `${FilterGroupOperator}`;
  filters: FilterPayload[];
  searchQuery: string | null;
}

export type LayoutParam = SwimlaneParams | { type: 'list' | 'table'; swimlane?: never };

export interface FetchRecordsPayload extends LayoutRecordsCountPayload {
  group: GroupParams | null;
  sorting?: ViewsSorting | null;
  layout?: LayoutRecordsParams | null;
}

export interface UpdateViewPayload extends CreateViewPayload {
  // TODO cant update predefined view
  id: number | string;
}

export interface BaseView {
  id: number | string;
  codename: string | null;
  dataSourceId: string;
  name: string;
  symbol: AnyViewSymbol | null;
  layout: ViewLayout;
  type: 'view';
}

export interface ViewGroup {
  id: number | string;
  codename: string | null;
  dataSourceId: string;
  name: string;
  symbol: AnyViewSymbol | null;
  type: 'view_group';
  views: BaseView[];
}

export type ViewListItem = BaseView | ViewGroup;

export interface ViewField {
  configId: string;
  selected: boolean;
}

export interface View extends Omit<BaseView, 'type'> {
  operator: `${FilterGroupOperator}`;
  filters: ViewFilterValue[];
  group: GroupConfig | null;
  quickFilters: ViewFilterDef[] | null;
  searchQuery: string | null;
  sorting: ViewsSorting | null;
  swimlaneId: string | null;
  visibility: Visibility;
  emptyState: ViewEmptyState | null;
  predefined: boolean;
}

export interface ViewSnapshot extends Omit<View, 'id'> {
  id: null;
}

export type { AnyViewSymbol };
